import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { NULL_VALUE } from 'constants/displayConstants';
import {
  Ics204SelectField,
  Ics204TextField,
  type Ics204SelectOption,
} from './Ics204Fields';
import { ICS204_TABLE } from './assignmentCardStyles';
import {
  PERSONNEL_ROLES,
  type PersonnelContactField,
  type PersonnelNameField,
} from './personnelSection';

type ViewRow = {
  position: string;
  name: string;
  contact: string;
  nameTestId: string;
  contactTestId: string;
};

type PersonnelSectionTableViewProps = {
  rows: ViewRow[];
};

const cellText = (value: string) => (value.trim() ? value : NULL_VALUE);

/** Read-only Position | Name | Contact Information table for section 4. */
export const PersonnelSectionTableView = ({
  rows,
}: PersonnelSectionTableViewProps) => {
  return (
    <Table className={ICS204_TABLE} data-testid="ics204-personnel-table">
      <TableHeader>
        <TableRow>
          <TableHead className="tw:w-[28%]">Position</TableHead>
          <TableHead className="tw:w-[36%]">Name</TableHead>
          <TableHead className="tw:w-[36%]">Contact Information</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.position}>
            <TableCell className="tw:align-middle tw:font-medium tw:whitespace-normal">
              {row.position}
            </TableCell>
            <TableCell
              className="tw:align-middle tw:whitespace-normal tw:text-foreground"
              data-testid={row.nameTestId}
            >
              {cellText(row.name)}
            </TableCell>
            <TableCell
              className="tw:align-middle tw:whitespace-normal tw:text-foreground"
              data-testid={row.contactTestId}
            >
              {cellText(row.contact)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

type PersonnelSectionTableEditProps = {
  options: Ics204SelectOption[];
  onNameSelect: (
    nameField: PersonnelNameField,
    contactField: PersonnelContactField,
    nextName: string
  ) => void;
};

/** Editable Position | Name | Contact Information table for section 4. */
export const PersonnelSectionTableEdit = ({
  options,
  onNameSelect,
}: PersonnelSectionTableEditProps) => {
  return (
    <Table className={ICS204_TABLE} data-testid="ics204-personnel-table">
      <TableHeader>
        <TableRow>
          <TableHead className="tw:w-[28%]">Position</TableHead>
          <TableHead className="tw:w-[36%]">Name</TableHead>
          <TableHead className="tw:w-[36%]">Contact Information</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {PERSONNEL_ROLES.map((role) => (
          <TableRow key={role.position}>
            <TableCell className="tw:align-middle tw:font-medium tw:whitespace-normal">
              {role.position}
            </TableCell>
            <TableCell className="tw:align-middle tw:whitespace-normal">
              <Ics204SelectField
                fieldName={role.nameField}
                testId={role.nameTestId}
                options={options}
                placeholder="Select from roster…"
                onSelectChange={(next) => {
                  onNameSelect(role.nameField, role.contactField, next);
                }}
              />
            </TableCell>
            <TableCell className="tw:align-middle tw:whitespace-normal">
              <Ics204TextField
                fieldName={role.contactField}
                testId={role.contactTestId}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
